// ClosetPicker.jsx
// お気に入りクローゼット（D案①-2・2026-10-15）: 注文確認画面で「このバッグに入れる服」を選ぶ欄。
// 撮影割引（AIが判定できた服を初めて出すときに1点 −100円・ワイシャツは対象外）の額は API の closet/index.php が返す
// photo_discount をそのまま使う。実際に引く額は注文確定（order/create.php）でサーバが決める。
// dev ビルドのクリーニングのバッグ注文（pack）だけで出す。API が enabled:0（本番）のときは何も出さない。
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Post } from './Post';
import { ItemIcon } from '../screens/Closet';
import { closetSelectionAfterLoad } from './closetSelection';

export const CLOSET_ORDER_ENABLED = process.env.REACT_APP_IS_DEV === 'true';

// 選んだ服の撮影割引の合計（表示用）
export const closetPhotoNebi = (data) =>
  (data?.closet_items || []).reduce((s, it) => s + Number(it.photo_discount || 0), 0);

// この注文で服を選べるか（新規のバッグ注文だけ。追加決済・再決済・バッグ単独・ふとんは不可＝API と同じ条件）
export const canPickCloset = (data) =>
  CLOSET_ORDER_ENABLED && data?.item?.type === 'pack' && !data?.order?.id;

export const ClosetPicker = ({ data, setData, reloadKey }) => {
  const [closet, setCloset] = useState(null);
  const [open, setOpen] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      const r = await Post('post', '/closet/index.php', {});
      if (!alive) return;
      if (r && r.result === 1) {
        setCloset(Number(r.enabled) === 1 ? r : { enabled: 0 });
      } else {
        // 読めなかった: 「読み直す」を出す
        setCloset({ enabled: 1, failed: 1 });
      }
      // 選択中の服を最新の状態（割引額・注文中）に差し替える。消えた服・注文中になった服は外す。
      // 無効になった・読めなかったときは全部外す（見えない服の割引が合計に残らないように。#721）
      setData((prev) => {
        const cur = prev.closet_items || [];
        const next = closetSelectionAfterLoad(r, cur);
        const same = next.length === cur.length && next.every((it, i) => it.id === cur[i].id && Number(it.photo_discount) === Number(cur[i].photo_discount));
        return same ? prev : { ...prev, closet_items: next };
      });
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey, retry]);

  if (!closet || Number(closet.enabled) !== 1) return null;
  if (closet.failed) {
    return (
      <div className="">
        <div className="title">このバッグに入れる服（任意）</div>
        <div className="content" style={{ fontSize: 13 }}>
          お気に入りクローゼットを読み込めませんでした。
          <span className="text-primary" style={{ cursor: 'pointer', textDecoration: 'underline', marginLeft: 6 }}
            onClick={() => { setCloset(null); setRetry((n) => n + 1); }}>読み直す</span>
          <div style={{ fontSize: 12, color: '#5a6166', marginTop: 4 }}>服を選ばずにそのままご注文いただけます。</div>
        </div>
      </div>
    );
  }
  const items = closet.items || [];
  const selected = data?.closet_items || [];
  const isOn = (id) => selected.some((it) => it.id === id);
  const yen = Number(closet.photo_discount_yen || 100);

  const toggle = (it) => {
    if (Number(it.in_order) === 1) return;
    setData((prev) => {
      const cur = prev.closet_items || [];
      const next = cur.some((x) => x.id === it.id) ? cur.filter((x) => x.id !== it.id) : [...cur, it];
      return { ...prev, closet_items: next };
    });
  };
  const nebi = closetPhotoNebi(data);

  return (
    <div className="">
      <div className="title">このバッグに入れる服（任意）</div>
      <div className="content">
        {selected.length > 0 && (
          <div style={{ fontSize: 13, marginBottom: 8 }}>
            {selected.length}点を選んでいます
            {nebi > 0 && <span className="text-danger">（撮影割引 -¥{nebi.toLocaleString()}）</span>}
          </div>
        )}
        <button type="button" className="btn btn-oval form-control" onClick={() => setOpen(!open)}
          style={{ border: '1.5px solid #15803d', color: '#15803d', background: '#fff', fontWeight: 700 }}>
          👗 お気に入りクローゼットから選ぶ
          <small style={{ display: 'block', fontSize: 11, fontWeight: 400, color: '#5a6166' }}>以前撮影した服をタップするだけ</small>
        </button>
        {open && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 12, color: '#5a6166', lineHeight: 1.6, marginBottom: 8 }}>
              📷 撮影割引：AIが判定できた服を<b>初めてクリーニングに出すとき</b>、1点につき <b>-¥{yen}</b>（ワイシャツは対象外）。
            </div>
            {items.length === 0 ? (
              <div style={{ fontSize: 13, color: '#666', padding: '8px 0' }}>
                まだ撮影した服がありません。<Link to="/closet">お気に入りクローゼット</Link>から撮影して追加できます。
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {items.map((it) => {
                  const on = isOn(it.id);
                  const busy = Number(it.in_order) === 1;
                  const d = Number(it.photo_discount || 0);
                  return (
                    <div key={it.id} className="div_btn" onClick={() => toggle(it)}
                      style={{
                        position: 'relative', border: `1.5px solid ${on ? '#15803d' : '#ddd'}`, background: on ? '#f2fbf4' : '#fff',
                        borderRadius: 12, padding: 10, textAlign: 'left', opacity: busy ? 0.5 : 1,
                      }}>
                      <div style={{
                        position: 'absolute', top: 8, right: 8, width: 20, height: 20, borderRadius: '50%', fontSize: 12, lineHeight: '18px',
                        textAlign: 'center', color: '#fff', border: `1.5px solid ${on ? '#15803d' : '#cfd4d1'}`, background: on ? '#15803d' : '#fff', zIndex: 1,
                      }}>{on ? '✓' : ''}</div>
                      <div style={{ width: '100%', height: 92, borderRadius: 8, background: '#f3f4f3', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5a6166' }}>
                        {it.thumb_url ? <img src={it.thumb_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <ItemIcon itemKey={it.item_key || 'other'} />}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, marginTop: 6 }}>{it.item_label || '品目未選択'}</div>
                      {it.item_name && <div style={{ fontSize: 11, color: '#5a6166', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.item_name}</div>}
                      <div style={{ fontSize: 11, marginTop: 2, color: busy ? '#5a6166' : d > 0 ? '#C50018' : '#8b9196' }}>
                        {busy ? '別のご注文に入っています' : d > 0 ? `初めてのお出しで -¥${d}` : it.item_key === 'shirt' ? 'ワイシャツは割引対象外' : Number(it.sent) === 1 ? '割引は適用済み' : !it.ai_item_type ? 'AIが判定できず割引対象外' : '割引対象外'}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
